import { buildTokenDetailPath } from './tokenProfiles'
import { ChainId } from '../sdk'
import {
  addressesEquivalent,
  toCatalogAddress,
} from '../data/marketApi/normalizeMarketToken'
import type { PoolSummary } from '../data/marketApi/types'

export type PoolTokenRef = {
  address: string
  symbol: string
  catalogAddress?: string
  role?: string
}

/** Perfil de pool para UI — alineado con `PoolSummary` del market API. */
export type LiquidityPoolProfile = PoolSummary

export type TokenLiquiditySummary = {
  poolCount: number
  totalTvl: string
  totalTvlUsd: number
  volume24h: string
  topPool: PoolSummary | null
}

function normalizeAddress(address: string): string {
  return address.toLowerCase()
}

function formatUsdCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return `$${value.toFixed(0)}`
}

export function summarizeTokenLiquidity(pools: PoolSummary[]): TokenLiquiditySummary {
  const totalTvlUsd = pools.reduce((sum, pool) => sum + pool.tvlUsd, 0)
  const sorted = [...pools].sort((a, b) => b.tvlUsd - a.tvlUsd)
  const volumeUsd = pools.reduce((sum, pool) => {
    const raw = pool.volume24h.replace(/[^0-9.]/g, '')
    const parsed = Number.parseFloat(raw)
    return sum + (Number.isFinite(parsed) ? parsed : 0)
  }, 0)

  return {
    poolCount: pools.length,
    totalTvl: pools.length ? formatUsdCompact(totalTvlUsd) : '—',
    totalTvlUsd,
    volume24h: pools.length ? formatUsdCompact(volumeUsd) : '—',
    topPool: sorted[0] ?? null,
  }
}

export function poolIncludesToken(pool: PoolSummary, tokenAddress: string, chainId: ChainId): boolean {
  return (
    addressesEquivalent(pool.token0.address, tokenAddress, chainId) ||
    addressesEquivalent(pool.token1.address, tokenAddress, chainId)
  )
}

export function poolTokenCatalogAddress(ref: PoolTokenRef, chainId: ChainId): string {
  void chainId
  return toCatalogAddress(ref.catalogAddress ?? ref.address)
}

export function buildLiquidityExplorePath(): string {
  return '/explore?tab=pools'
}

export function buildPoolDetailPath(poolId: string): string {
  return `/liquidity/${poolId}`
}

export function buildTokenLiquidityPath(tokenAddress: string): string {
  return buildTokenDetailPath(tokenAddress, 'liquidity')
}
